import { useTranslation } from 'react-i18next';
import {
  useAllInstallationsQuery,
  useCompaniesQuery,
  useCreateInstallationMutation,
  useUpdateInstallationMutation,
  useDeleteInstallationMutation,
  useInstallationFilesQuery,
  useUploadInstallationFileMutation,
  useDeleteFileMutation,
} from 'src/queries';
import { useModal } from 'src/hooks/useModal';
import type { Installation, Company } from 'src/types';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import EntityModal from '../components/EntityModal';
import ConfirmDialog from '../components/ConfirmDialog';
import FileManager from '../components/FileManager';
import InstallationForm from '../forms/InstallationForm';

const InstallationsTab = () => {
  const { t } = useTranslation();
  const { data: installations = [], isLoading } = useAllInstallationsQuery();
  const { data: companies = [] } = useCompaniesQuery();

  const createMutation = useCreateInstallationMutation();
  const updateMutation = useUpdateInstallationMutation();
  const deleteMutation = useDeleteInstallationMutation();

  const formModal = useModal<Installation>();
  const deleteModal = useModal<Installation>();

  // File management for the currently editing installation
  const editingInstallationId = formModal.editingItem?.id ?? null;
  const { data: installationFiles = [], isLoading: filesLoading } =
    useInstallationFilesQuery(editingInstallationId);
  const uploadMutation = useUploadInstallationFileMutation(editingInstallationId);
  const deleteFileMutation = useDeleteFileMutation();

  const companyMap = new Map<string, Company>(companies.map((c) => [c.id, c]));

  const columns: Column<Installation>[] = [
    { key: 'name', header: t('management.installation.name') },
    {
      key: 'company_id',
      header: t('management.installation.company'),
      render: (i) => companyMap.get(i.company_id ?? '')?.name ?? '-',
    },
    {
      key: 'location',
      header: t('management.installation.address'),
      render: (i) => i.location?.address ?? '-',
    },
    {
      key: 'measuring_system',
      header: t('management.installation.measuringSystem'),
      render: (i) => i.installation_metadata?.measuring_system ?? i.measuring_system ?? '-',
    },
  ];

  const handleCreate = (data: Partial<Installation>) => {
    createMutation.mutate(data, { onSuccess: () => formModal.close() });
  };

  const handleUpdate = (data: Partial<Installation>) => {
    if (!formModal.editingItem) return;
    updateMutation.mutate(
      { id: formModal.editingItem.id, data },
      { onSuccess: () => formModal.close() }
    );
  };

  const handleDelete = () => {
    if (!deleteModal.editingItem) return;
    deleteMutation.mutate(deleteModal.editingItem.id, {
      onSuccess: () => deleteModal.close(),
    });
  };

  const isEditing = !!formModal.editingItem;

  return (
    <>
      <DataTable
        columns={columns}
        data={installations}
        isLoading={isLoading}
        onCreate={formModal.openForCreate}
        onEdit={(inst) => formModal.openForEdit(inst)}
        onDelete={(inst) => deleteModal.openForEdit(inst)}
        createLabel={t('management.installations')}
      />

      <EntityModal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? t('management.edit') : t('management.create')}
        width="640px"
      >
        <InstallationForm
          initialData={formModal.editingItem ?? undefined}
          companies={companies}
          onSubmit={isEditing ? handleUpdate : handleCreate}
          isLoading={isEditing ? updateMutation.isPending : createMutation.isPending}
          onCancel={formModal.close}
        />
        {isEditing && (
          <FileManager
            files={installationFiles}
            isLoading={filesLoading}
            onUpload={(file) => uploadMutation.mutate(file)}
            onDelete={(fileId) => deleteFileMutation.mutate(fileId)}
            isUploading={uploadMutation.isPending}
            accept="image/*"
            label={t('management.installation.siteImages')}
          />
        )}
      </EntityModal>

      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onConfirm={handleDelete}
        onCancel={deleteModal.close}
        title={t('management.confirmDeleteTitle')}
        message={t('management.confirmDeleteMessage', {
          name: deleteModal.editingItem?.name ?? '',
        })}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default InstallationsTab;
